import { createServerFn } from "@tanstack/react-start"
import { requireAuth, requireScopes, rateLimitFunction, createPostHogTrackingMiddleware } from "~/lib/middleware"
import { ApiResponse, ApiKey, apiKeySchema } from "~/lib/schemas"
import z from "zod"
import { LogtoScopes } from "~/lib/logto/logto"
import { ScopeRule } from "~/lib/scopes"

export const updateApiKeySchema = z.object({
  key_id: z.string().min(1, 'API key ID is required'),
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters').optional(),
  description: z.string().max(500, 'Description must be less than 500 characters').nullable().optional(),
  expires_in: z.number().optional(),
  scopes: z.array(z.string()).optional(),
  is_active: z.boolean().optional(),
})

export type UpdateApiKeyRequest = z.infer<typeof updateApiKeySchema>

export const updateApiKey = createServerFn({
  method: 'POST',
})
.middleware([requireAuth, rateLimitFunction, requireScopes(ScopeRule.OR([LogtoScopes.AdminOrgKeysCrud, ScopeRule.ORG(LogtoScopes.OrgKeyCrud)])), createPostHogTrackingMiddleware('update_api_key')])
.validator(updateApiKeySchema)
  .handler(async (ctx) => {
    const apiKeyData = ctx.data
    
    // Use real API
    const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
    const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
    
    if (!BFF_API_KEY) {
      throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
    }

    //if this is a org token, then check that the orgs match
    if(ctx.context.isOrgToken) {
      const params = new URLSearchParams({
        organization_id: ctx.context.accessToken.organization_id!,
        // sort_by: "created_at",
        // sort_order: "desc"
      })
      
      const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations/keys/info?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${BFF_API_KEY}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
      }

      

      const result = await response.json() as ApiResponse<ApiKey[]>
      const apiKeys = z.array(apiKeySchema).parse(result.data ?? [])
      console.log("apiKeys: ", result, ctx.context.accessToken.organization_id)
      const apiKey = apiKeys.find(key => key.key_id === apiKeyData.key_id)
      if(!apiKey) {
        throw new Error('You are not authorized to update this API key')
      }

    }
    
    const params = new URLSearchParams({ key_id: apiKeyData.key_id })
    
    const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/api-keys?${params}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${BFF_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: apiKeyData.name,
        description: apiKeyData.description,
        expires_in: apiKeyData.expires_in,
        scopes: apiKeyData.scopes,
        is_active: apiKeyData.is_active,
      }),
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }

    
    
    const result = await response.json() as ApiResponse<ApiKey>
    return apiKeySchema.parse(result.data)
  }) 
